import * as React from 'react';
import { useState, useEffect, useRef } from 'react';

import Table from 'react-bootstrap/Table';
import Button from 'react-bootstrap/Button';

import { CustomProgressBar } from '../CustomProgressBar/CustomProgressBar';
import LoadingSpinner from 'sos-common/js/components/LoadingSpinner/LoadingSpinner';
import { makeTranslator } from '../../utilities/translations';
import { Icon } from '../../entries/components';
import { ButtonModal } from './../ButtonModal/ButtonModal';
import {
    importKey,
    KeyData,
} from '../FileAPIClient/Cryptography';

import {
    formatDateYYYYMMDDHHMM,
    formatDiarienummer,
    formatFileSizeWithNormalizedUnit,
} from '../CaseList/Support';

import {
    FileSessionId,
    FileSessionVM,
    FileId,
} from '../FileAPIClient/Api.types';

import {
    FileAPIClient
} from '../FileAPIClient/FileApiClient';

import {
    FileDownloadProps
} from './FileDownload.types';

import {
    DownloadFileTracker,
    FileTrackerStatusEnum,
} from './DownloadFileTracker';

import {
    Queue,
} from './Queue';

import {
    Downloader,
} from './Downloader';

import './FileDownload.scss';

export const FileDownload: React.FC<FileDownloadProps> = (props) => {
    const Translate = makeTranslator(props.translation);

    const [isAuthenticated, setIsAuthenticated] = useState(true);

    const [errorMessage, setErrorMessage] = useState<string>('');
    const [isErrorModalOpen, setIsErrorModalOpen] = useState<boolean>(false);
    const [isConfirmAbortFileDownloadModalOpen, setIsConfirmAbortFileDownloadModalOpen] = useState(false);
    const [screenReaderMessage, setScreenReaderMessage] = useState<string>('');
    const [rowIndexForModal, setRowIndexForModal] = useState<number>();

    const [forceRerender, setForceRerender] = useState<boolean>(false);
    const [fileTrackers, setFileTrackers] = useState<DownloadFileTracker[]>([]);
    const [fileSession, setFileSession] = useState<FileSessionVM>();
    const [isQueueRunning, setIsQueueRunning] = useState<boolean>(false);
    const [isDownloadInProgress, setIsDownloadInProgress] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingText, setLoadingText] = useState<string>();

    const [hasSymmetricKey, setHasSymmetricKey] = useState(false);
    const [retryCount, setRetryCount] = useState(0);
    const [autosave, aetAutosave] = useState<boolean>(true);

    const selectFileMessage = Translate('reselectfile');

    const symmetricKeyRef = useRef<CryptoKey>();
    const symmetricIvRef = useRef<ArrayBuffer>();
    const minutApiRef = useRef<FileAPIClient>(new FileAPIClient((isAuthenticated: boolean) => { setIsAuthenticated(isAuthenticated); }));
    const queueRef = useRef<Queue>(new Queue(() => { queueChanged(); }));
    const downloaderRef = useRef<Downloader>(null);

    // ##############
    // ### Setup ####
    // ##############

    const initComponent = async () => {
        setIsLoading(true);
        try {
            //setLoadingText(Translate('fetchingcaseinfo'));
            let apiClient = minutApiRef.current;
            if (!apiClient) {
                throw new Error(Translate('/error/somethingwentwrong'));
            }

            await apiClient.isAuthenticated();
            if (!isAuthenticated) {
                throw new Error(Translate('/error/accesscheck'));
            }

            downloaderRef.current = new Downloader(() => { downloaderChanged(); }, () => { downloaderDone(); }, (message: string) => { handleWorkerError(message); }, minutApiRef.current)

            let id: FileSessionId = { value: props.fileSessionId };
            const fileSessionResponse = await apiClient.getFileSession(id);
            setFileSession(fileSessionResponse);

            await apiClient.requestSymmetricKey(id);

            await registerStoredFilesWithFileTracker(apiClient, fileSessionResponse.files);
        } catch (error) {
            openErrorModal((error as Error).message);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        initComponent();
    }, []);

    // ###########################
    // ### Callbacks ####
    // ###########################

    const queueChanged = () => {
        setForceRerender(prev => !prev);
        setIsQueueRunning(queueRef.current.running);
    }

    const downloaderChanged = () => {
        if (downloaderRef.current.hasNoNext) {
            setIsDownloadInProgress(false);
        }
        const fileTracker = downloaderRef.current.activeItem;
        setForceRerender(prev => !prev);
        if (downloaderRef.current.activeItem.status === FileTrackerStatusEnum.Downloading) {
            setScreenReaderMessage(
                `${Translate('downloadof')} ${fileTracker.storedFile!.name} ${Translate('is')} ${fileTracker.progress}% ${Translate('done')}`
            );
        }
        if (downloaderRef.current.activeItem.status === FileTrackerStatusEnum.Verifying) {
            downloaderRef.current.activeItem.infoMsg = `${Translate('checkfile')}`;
            setScreenReaderMessage(
                `${Translate('checkfile')} ${fileTracker.storedFile!.name}`
            );
        }
    }

    const downloaderDone = () => {
        if (downloaderRef.current.activeItem.status === FileTrackerStatusEnum.Done) {
            setScreenReaderMessage(
                `${Translate('thefile')} ${downloaderRef.current.activeItem.storedFile!.name} ${Translate('hasbeendownloaded')}.`
            );
        }
        downloaderRef.current.clear();
        setIsDownloadInProgress(false);
    }

    const handleWorkerError = (message: string) => {
        downloaderRef.current.activeItem.status = FileTrackerStatusEnum.Error;
        downloaderRef.current.activeItem.errorMsg = `${Translate(message)}`;

        setIsDownloadInProgress(false);
    }

    // ###########################
    // ### Effects ####
    // ###########################

    // Process för hämtning av symmetrisk nyckel
    useEffect(() => {
        if (symmetricKeyRef.current) {
            // Reset retry count when we get a key
            setRetryCount(0);
            return;
        }

        const getRetryDelay = (count: number): number => {
            if (count === 0) return 1000;  // 1 second for first attempt
            return 6000;                   // 6 seconds for subsequent attempts
        };

        const timeoutId = setTimeout(() => {
            setupSymmetricKey();
            setRetryCount(prev => prev + 1);
        }, getRetryDelay(retryCount));

        return () => clearTimeout(timeoutId);
    }, [fileSession, retryCount]);

    // Process för att förnya användarsession medan nedladdning pågår
    useEffect(() => {
        if (!isQueueRunning) {
            return;
        }

        const intervalId = setInterval(() => {
            refreshUserSession();
        }, 15 * 60 * 1000);

        return () => clearInterval(intervalId);
    }, [isQueueRunning]);

    // Process för att köra nedladdningskö
    useEffect(() => {
        if (isQueueRunning && !isDownloadInProgress && hasSymmetricKey && !isLoading) {
            processQueue();
        }
    }, [isQueueRunning, isDownloadInProgress, hasSymmetricKey, isLoading]);

    // ######################
    // ### File handling ####
    // ######################

    // Läser in fildata från API
    const registerStoredFilesWithFileTracker = async (
        apiClient: FileAPIClient,
        files: FileId[]
    ) => {
        if (!files || files.length === 0) {
            return;
        }
        if (!apiClient) {
            throw new Error(Translate('/error/somethingwentwrong'));
        }

        const newFileTrackers: DownloadFileTracker[] = [];

        for (const [index, fileId] of files.entries()) {
            setLoadingText(`${Translate('fetchingfile')} ${index + 1} ${Translate('of')} ${files.length}`);

            const fileInfo = await apiClient.getFile(fileId);
            const newFileTracker = new DownloadFileTracker(fileInfo);

            if (newFileTracker.status === FileTrackerStatusEnum.Paused) {
                newFileTracker.infoMsg = selectFileMessage;
            }

            newFileTrackers.push(newFileTracker);
        }

        const loadedFileTrackers = [...fileTrackers, ...newFileTrackers];
        setFileTrackers(loadedFileTrackers);
    };

    // Skapar lokal fil för nedladdning
    const selectNewFile = async (fileTrackerIndex: number): Promise<boolean> => {
        let fileHandle: FileSystemFileHandle | undefined = undefined;

        fileTrackers[fileTrackerIndex].clearMessages();

        try {
            const options = {
                suggestedName: fileTrackers[fileTrackerIndex].storedFile?.name,
            };

            fileHandle = await (window as any).showSaveFilePicker(options);
        } catch (e) {
            const error = e as Error;
            console.error('showSaveFilePicker failed:', {
                name: error.name,
                message: error.message,
                stack: error.stack,
                fullError: error
            });

            if (error.name === 'AbortError') {
                return false;
            } else {
                throw Error(Translate('/error/selectfilefailed'));
            }
        }

        fileTrackers[fileTrackerIndex].fileHandle = fileHandle;
        fileTrackers[fileTrackerIndex].isNewFile = true;

        return true;
    };

    // Skapar lokal fil för återupptagning av nedladdning
    const selectOngoingFile = async (fileTrackerIndex: number): Promise<boolean> => {
        let fileHandle: FileSystemFileHandle | undefined = undefined;

        try {
            [fileHandle] = await (window as any).showOpenFilePicker({
                multiple: false,
            });
        } catch (error) {
            if ((error as Error).name === 'AbortError') {
                return false;
            } else {
                throw new Error(Translate('/error/selectfilefailed'));
            }
        }

        if (!fileHandle) {
            throw new Error(Translate('/error/fileoperationfailed'));
        }

        // showOpenFilePicker only grants 'read' permission by default. Resuming a
        // download needs to write to the file, so 'readwrite' permission must be
        // explicitly requested, otherwise createWritable() will throw NotAllowedError.
        const permissionOptions = { mode: 'readwrite' as const };
        let permissionStatus = await (fileHandle as any).queryPermission(permissionOptions);
        if (permissionStatus !== 'granted') {
            permissionStatus = await (fileHandle as any).requestPermission(permissionOptions);
        }
        if (permissionStatus !== 'granted') {
            throw new Error(Translate('/error/selectfilefailed'));
        }

        fileTrackers[fileTrackerIndex].fileHandle = fileHandle;
        fileTrackers[fileTrackerIndex].isNewFile = false;

        return true;
    };

    const processQueue = async () => {
        setIsDownloadInProgress(true);
        const next = queueRef.current.dequeueNext();
        if (!next) {
            return;
        }
        try {
            downloaderRef.current.start(next.value, symmetricKeyRef.current, symmetricIvRef.current, autosave);
        } catch (error) {
            console.error('Queue processing error:', error);
            next.value.status = FileTrackerStatusEnum.Error;
        }
    }

    const setupSymmetricKey = async () => {
        try {
            if (symmetricKeyRef.current) {
                // Om vi redan har en nyckel, gör inget
                return;
            }

            let key: KeyData | null = null;
            try {
                key = await minutApiRef.current.getSymmetricKey(fileSession.id);
            } catch {
                console.warn('Fel vid hämtande av symmetrisk nyckel');
                return;
            }

            if (!key) {
                console.log('Väntar på att krypteringsnyckel ska bli tillgänglig');
                return;
            }

            symmetricIvRef.current = key.iv || new ArrayBuffer(0);

            const encodedKey = await importKey(key.key);
            symmetricKeyRef.current = encodedKey;
            setHasSymmetricKey(true);
        } catch {
            throw new Error(Translate('/error/encryptionkeys'));
        }
    };

    const refreshUserSession = async () => {
        if (!isDownloadInProgress) {
            return;
        }

        await minutApiRef.current.refreshUserSession();
    }

    // ##########################
    // ### UserInput Buttons ####
    // ##########################

    // Event handler för klick på nedladdning-knappen för att påbörja en ny nedladdning.
    const handleNewDownload = async (fileTrackerIndex: number) => {
        if (!fileSession || !fileTrackers[fileTrackerIndex].storedFile?.id) {
            throw new Error(Translate('/error/somethingwentwrong'));
        }

        try {
            const hasSelectedFile = await selectNewFile(fileTrackerIndex);

            if (hasSelectedFile) {
                // Reset any previous download state
                try {
                    setIsLoading(true);
                    await minutApiRef.current.abortFileDownload(fileTrackers[fileTrackerIndex].storedFile.id);
                } catch (error) {
                    // Ignore abort errors for files not currently being downloaded
                } finally {
                    setIsLoading(false);
                }

                queueRef.current.add(fileTrackers[fileTrackerIndex], fileTrackerIndex);
            }
        } catch (error) {
            fileTrackers[fileTrackerIndex].errorMsg = (error as Error).message;
            queueRef.current.remove(fileTrackerIndex);
        }
    };

    // Event handler för klick på paus-knappen för att pausa pågående nedladdning.
    const handlePauseDownload = (index: number) => {
        if (downloaderRef.current) {
            downloaderRef.current.stop();
        }
    };

    // Event handler för klick på återuppta-knappen för att påbörja en pausad nedladdning.
    const handleResumeDownload = async (fileTrackerIndex: number) => {
        const fileTracker = fileTrackers[fileTrackerIndex];
        fileTracker.clearMessages();

        try {
            if (await selectOngoingFile(fileTrackerIndex)) {
                fileTrackers[fileTrackerIndex].isNewFile = false;
                queueRef.current.add(fileTracker, fileTrackerIndex);
            }
        } catch (error) {
            fileTrackers[fileTrackerIndex].errorMsg = (error as Error).message;
            queueRef.current.remove(fileTrackerIndex);
        }
    };

    // Event handler för klick på ta bort från kö knappen så filen inte längre väntar på sin tur.
    const handleDequeueDownload = (fileTrackerIndex: number) => {
        queueRef.current.remove(fileTrackerIndex);
    };

    // Event handler för klick på krysset för att ta bort en påbörjad nedladdning.
    const handleConfirmAbortFileDownload = (fileTrackerIndex: number) => {
        setRowIndexForModal(fileTrackerIndex);
        setIsConfirmAbortFileDownloadModalOpen(true);
    };

    const handleAbortFileDownload = async (fileTrackerIndex: number) => {
        setIsConfirmAbortFileDownloadModalOpen(false);
        setIsLoading(true);
        try {
            const fileTracker = fileTrackers[fileTrackerIndex];
            await minutApiRef.current.abortFileDownload(fileTracker.storedFile?.id);
            fileTracker.abort();

            fileTracker.clearMessages();
        } catch (error) {
            fileTrackers[fileTrackerIndex].errorMsg = `${Translate('/error/abortdownloadfailed')} ${fileTrackers[fileTrackerIndex].name}`;
        } finally {
            setIsLoading(false);
        }
    };

    const handleCloseConfirmAbortFileDownloadModal = () => {
        setIsConfirmAbortFileDownloadModalOpen(false);
    };

    const handleStartQueue = () => {
        queueRef.current.start();
    };

    const handleStopQueue = () => {
        queueRef.current.stop();
    };

    const handleDequeueAllFiles = () => {
        queueRef.current.removeAll();
    };

    const onErrorModalClick = () => {
        setErrorMessage('');
        setIsErrorModalOpen(false);
        document.location.replace(props.pageLinkMyCases);
    };

    const openErrorModal = (message: string) => {
        setErrorMessage(message);
        setIsErrorModalOpen(true);
    };

    // ###########################
    // ### Användargränssnitt ####
    // ###########################

    return (
        <>
            {props.pageHeading && <h1>{fileSession?.arende.title}</h1>}
            {<p className="">{formatDiarienummer(fileSession?.arende.diarienummer)}</p>}
            {props.pageBody && <p dangerouslySetInnerHTML={{ __html: props.pageBody }}></p>}
            {<h2>{Translate('sidtitel')}</h2>}

            {!hasSymmetricKey && <div className="down-file-tracker__warningpanel">
                <LoadingSpinner darkColor={true} />
                <p>
                    {Translate('fetchingsymmetrickeys')}
                </p>
            </div>}

            <div aria-busy={isLoading} className="down-file-tracker">
                <Table className="file-tracker-table" aria-label={Translate('filedownloads')}>
                    <thead>
                        <tr>
                            <th className="down-file-tracker__table-header file-tracker__table-header--first">
                                {Translate('filnamn')}
                            </th>
                            <th className="down-file-tracker__table-header file-tracker__table-header--size">
                                {Translate('filstorlek')}
                            </th>
                            <th className="down-file-tracker__table-header file-tracker__table-header--date">
                                {Translate('nedladdningsdatum')}
                            </th>
                            <th className="down-file-tracker__table-header"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {fileTrackers.map((fileTracker: DownloadFileTracker, fileTrackerIndex) => (
                            <React.Fragment key={fileTrackerIndex}>
                                <tr className="down-file-tracker__table-row ">
                                    <td>{fileTracker.name}</td>
                                    <td>{formatFileSizeWithNormalizedUnit(fileTracker.size)}</td>
                                    <td>{fileTracker.storedFile?.processed ? formatDateYYYYMMDDHHMM(fileTracker.storedFile?.processed) : ""}</td>
                                    <td className="down-file-tracker__table-cell--right">
                                        {(fileTracker.isIdle)
                                            && !fileTracker.queued && (
                                            <button
                                                type="button"
                                                disabled={isLoading}
                                                aria-disabled={isLoading}
                                                className="down-file-tracker__select"
                                                onClick={() => handleNewDownload(fileTrackerIndex)}>
                                                <span>{Translate('laddaned') || 'Download'}</span>
                                            </button>
                                            )}
                                        {(fileTracker.isActive) && (
                                            <button
                                                type="button"
                                                disabled={isLoading}
                                                aria-disabled={isLoading}
                                                className="down-file-tracker__pause"
                                                onClick={() => handlePauseDownload(fileTrackerIndex)}>
                                                <span>{Translate('pausedownload') || 'Pause'}</span>
                                            </button>
                                        )}
                                        {(fileTracker.isPaused) && (
                                            <button
                                                type="button"
                                                disabled={isLoading}
                                                aria-disabled={isLoading}
                                                className="down-file-tracker__resume"
                                                onClick={() => handleResumeDownload(fileTrackerIndex)}>
                                                <span>{Translate('resumedownload') || 'Resume'}</span>
                                            </button>
                                        )}
                                        {fileTracker.isQueued && (
                                            <button
                                                title={Translate('removefromqueue') || 'Remove from queue'}
                                                disabled={isLoading}
                                                aria-disabled={isLoading}
                                                type="button"
                                                className="down-file-tracker__dequeue"
                                                aria-label={`${Translate('removefromqueue') || 'Remove from queue'} ${fileTracker.name}`}
                                                onClick={() => handleDequeueDownload(fileTrackerIndex)}>
                                                <span>{Translate('removefromqueue') || 'Remove from queue'}</span>
                                            </button>
                                        )}
                                        {(fileTracker.isPaused) && (
                                            <button
                                                title={Translate('abortdownload') || 'Abort'}
                                                disabled={isLoading}
                                                aria-disabled={isLoading}
                                                type="button"
                                                className="down-file-tracker__delete"
                                                aria-label={`${Translate('abortdownload') || 'Abort'} ${fileTracker.name}`}
                                                onClick={() => handleConfirmAbortFileDownload(fileTrackerIndex)}>
                                                <span>
                                                    <Icon iconType="fontawesome/close" />
                                                </span>
                                            </button>
                                        )}
                                    </td>
                                </tr>
                                <tr>
                                    <td colSpan={4} className="down-file-tracker__progress-wrapper">
                                        {(fileTracker.progress > 0) && (
                                            <div className="down-file-tracker__progress-container" aria-hidden="true">
                                                <div className="down-file-tracker__progress-bar">
                                                    <CustomProgressBar
                                                        animated={fileTracker.status >= FileTrackerStatusEnum.Downloading}
                                                        now={fileTracker.progress}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                        {fileTracker.status === FileTrackerStatusEnum.Verifying && (
                                            <div className="down-file-tracker__progress-container" aria-hidden="true">
                                                <div className="down-file-tracker__progress-bar">
                                                    <CustomProgressBar
                                                        animated={fileTracker.status === FileTrackerStatusEnum.Verifying}
                                                        now={fileTracker.verifyProgress}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                        {fileTracker.isQueued && (
                                            <div className="down-file-tracker__infopanel" aria-live="polite">
                                                <Icon iconType="fontawesome/clock" />
                                                {Translate('queuedfordownload') || 'Queued for download'}: {queueRef.current.getPosition(fileTrackerIndex)}
                                            </div>
                                        )}
                                        {fileTracker.hasInfo && (
                                            <div className="down-file-tracker__infopanel" aria-live="polite">
                                                <Icon iconType="fontawesome/exclamation-circle-solid" />
                                                {fileTracker.infoMsg}
                                            </div>
                                        )}
                                        {fileTracker.hasError && (
                                            <div className="down-file-tracker__errorpanel" aria-live="assertive">
                                                <Icon iconType="fontawesome/exclamation-circle-solid" />
                                                {fileTracker.errorMsg}
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            </React.Fragment>
                        ))}
                    </tbody>
                </Table>

                {(isLoading) && <LoadingSpinner darkColor={true} cssClasses="down-file-tracker__spinner" />}

                {!isLoading && fileTrackers.length === 0 && (
                    <p className={'down-file-tracker__nofiles'}>{Translate('nofilesfordownload')}</p>
                )}
                {isLoading && fileTrackers.length === 0 && <p className={'file-tracker__loadingfiles'}>{loadingText}</p>}
                <div className="sr-only" aria-live="assertive">
                    {screenReaderMessage}
                </div>

                <div className="file-actions m-b-2">
                    {isQueueRunning && (
                        <Button
                            className="button file-actions__button--left"
                            onClick={handleStopQueue}>
                            {Translate('stopqueue') || 'Stop queue'}
                        </Button>
                    )}
                    {!queueRef.current.active && (
                        <Button
                            className="button file-actions__button--left"
                            onClick={handleStartQueue}>
                            {Translate('startqueue') || 'Start queue'}
                        </Button>
                    )}
                    {queueRef.current.hasItems && (
                        <Button
                            className="button file-actions__button--left"
                            onClick={handleDequeueAllFiles}>
                            {Translate('removeallfromqueue1') || 'Remove all'} {queueRef.current.items.length} {Translate('removeallfromqueue2') || 'files from queue'}
                        </Button>
                    )}
                </div>



            </div>

            <div className="m-b-2">
                <label>
                    <input
                        type="checkbox"
                        checked={autosave}
                        onChange={(e) => aetAutosave(e.target.checked)}
                    />
                    {' '}
                    {Translate('autosave') || 'Autosave'}
                    {' '}
                    <Icon
                        iconType="Infocirkel_normal20px20px"
                        isAriaHidden={false}
                        ariaLabel={Translate('autosaveInfo') || ''}
                        title={Translate('autosaveInfo') || ''}
                    />
                </label>
            </div>

            <ButtonModal
                isModalOpen={isConfirmAbortFileDownloadModalOpen}
                passData={handleAbortFileDownload}
                buttonText={''}
                buttonCss={'button'}
                modalAction={''}
                modalHeading={Translate('canceldownload')}
                modalBody={
                    fileTrackers[rowIndexForModal!] && `${Translate('canceldownloadconfirmation')} "${fileTrackers[rowIndexForModal!].name}"?`
                }
                modalCancelText={Translate('no')}
                modalCancelCss={'button'}
                modalActionText={Translate('yes')}
                modalActionCss={'button'}
                closeConfirm={handleCloseConfirmAbortFileDownloadModal}
                rowIndex={rowIndexForModal}>
            </ButtonModal>

            <ButtonModal
                isModalOpen={isErrorModalOpen}
                passData={onErrorModalClick}
                modalHeading={Translate('/error/somethingwentwrong')}
                modalBody={errorMessage}
                modalActionText={Translate('close')}
                modalActionCss={'button'}
                closeConfirm={onErrorModalClick}>
            </ButtonModal>
        </>
    );
};
